/**
 * NIGHTBOUND - creature visual states.
 *
 * The asset system is driven by a small fixed vocabulary of states. A species
 * folder may contain art for any subset of them; anything missing falls back
 * to IDLE and then to a procedural placeholder (see CreatureRenderer), so
 * artwork can be added progressively without breaking the build.
 */

export const CREATURE_STATES = [
  'IDLE',
  'HAPPY',
  'HUNGRY',
  'DIRTY',
  'SLEEPING',
  'EATING',
  'TRAINING',
  'INJURED',
] as const

export type CreatureStateId = (typeof CREATURE_STATES)[number]

/** File suffix for each state: `dog_idle.webp`, `dog_training.webp`, ... */
export const STATE_FILE_SUFFIX: Record<CreatureStateId, string> = {
  IDLE: 'idle',
  HAPPY: 'happy',
  HUNGRY: 'hungry',
  DIRTY: 'dirty',
  SLEEPING: 'sleeping',
  EATING: 'eating',
  TRAINING: 'training',
  INJURED: 'injured',
}

/** Animation class applied when no dedicated art exists for a state. */
export const STATE_ANIMATION: Record<CreatureStateId, string> = {
  IDLE: 'nb-anim-idle',
  HAPPY: 'nb-anim-happy',
  HUNGRY: 'nb-anim-hungry',
  DIRTY: 'nb-anim-dirty',
  SLEEPING: 'nb-anim-sleeping',
  EATING: 'nb-anim-eating',
  TRAINING: 'nb-anim-training',
  INJURED: 'nb-anim-injured',
}

export interface CreatureCondition {
  hungry: boolean
  dirty: boolean
  exhausted: boolean
  injured: boolean
  disturbed: boolean
}

/**
 * Resolve which single piece of art/animation represents the creature right
 * now. Activity (something the player just did) wins over mood, because the
 * player needs to see the result of their own action.
 */
export function resolveCreatureState(input: {
  activity: string
  mood: string
  condition: CreatureCondition
}): CreatureStateId {
  switch (input.activity) {
    case 'SLEEPING':
      return 'SLEEPING'
    case 'EATING':
      return 'EATING'
    case 'TRAINING':
      return 'TRAINING'
    case 'PLAYING':
      return 'HAPPY'
    case 'CLEANING':
      return 'DIRTY'
    default:
      break
  }

  switch (input.mood) {
    case 'SLEEPING':
      return 'SLEEPING'
    case 'HUNGRY':
      return 'HUNGRY'
    case 'DIRTY':
      return 'DIRTY'
    case 'UNWELL':
      return 'INJURED'
    case 'HAPPY':
      return 'HAPPY'
    default:
      break
  }

  if (input.condition.injured) return 'INJURED'
  if (input.condition.hungry) return 'HUNGRY'
  if (input.condition.dirty) return 'DIRTY'
  return 'IDLE'
}
